import {
  ArrowLeft,
  BookOpen,
  Building2,
  ChevronRight,
  Ellipsis,
  LayoutGrid,
  Layers,
  List,
  PanelLeft,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';

import type { Board, BoardTreeNode, Course } from '@/types/api/course.types';
import type { ProjectResponse } from '@/types';
import type { User } from '@/types';
import { getInitials } from '@/lib';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

type BoardViewMode = 'cards' | 'hierarchy';
type DetailTab = 'overview' | 'courses' | 'projects' | 'people-access';

type BoardFormState = {
  board_name: string;
  description: string;
  head_of_board: string;
  parent: string;
};

type BoardEditorItem = Pick<
  Board,
  'id' | 'board_name' | 'description' | 'head_of_board' | 'parent'
>;

type HeaderProps = {
  currentBoardId?: number;
  selectedBoardName?: string;
  canManage: boolean;
  viewMode: BoardViewMode;
  onBack: () => void;
  onOpenCreate: () => void;
  onViewChange: (viewMode: BoardViewMode) => void;
};

export const BoardsHeader = ({
  currentBoardId,
  selectedBoardName,
  canManage,
  viewMode,
  onBack,
  onOpenCreate,
  onViewChange,
}: HeaderProps) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
    <div className="flex items-center gap-2">
      {!!currentBoardId && (
        <button
          type="button"
          onClick={onBack}
          className="rounded-full p-2 hover:bg-muted"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}
      {selectedBoardName ? (
        <h1 className="text-3xl font-bold tracking-tight">{selectedBoardName}</h1>
      ) : null}
    </div>
    <div className="flex items-center gap-2">
      {canManage && (
        <Button type="button" className="gap-2" onClick={onOpenCreate}>
          <Plus className="h-4 w-4" />
          {currentBoardId ? 'Add Sub-Board' : 'Add board'}
        </Button>
      )}
      <div className="inline-flex rounded-2xl border border-border bg-muted/60 p-1 dark:border-white/10 dark:bg-white/5">
        <button
          type="button"
          onClick={() => onViewChange('cards')}
          className={`rounded-xl p-2 transition-colors ${viewMode === 'cards' ? 'bg-background text-orange-600 shadow-sm dark:bg-slate-800 dark:text-orange-500' : 'text-muted-foreground hover:text-foreground dark:text-slate-300 dark:hover:text-white'}`}
          aria-label="Cards view"
        >
          <LayoutGrid className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onViewChange('hierarchy')}
          className={`rounded-xl p-2 transition-colors ${viewMode === 'hierarchy' ? 'bg-background text-orange-600 shadow-sm dark:bg-slate-800 dark:text-orange-500' : 'text-muted-foreground hover:text-foreground dark:text-slate-300 dark:hover:text-white'}`}
          aria-label="Hierarchy view"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

type BreadcrumbProps = {
  breadcrumbTrail: BoardTreeNode[];
  onRootClick: () => void;
  onJump: (index: number) => void;
};

export const BoardsBreadcrumb = ({
  breadcrumbTrail,
  onRootClick,
  onJump,
}: BreadcrumbProps) => (
  <div className="flex flex-wrap items-center gap-2 text-sm">
    <button
      type="button"
      onClick={onRootClick}
      className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 font-medium transition-colors ${
        breadcrumbTrail.length === 0
          ? 'border border-border bg-muted/40 text-foreground dark:border-white/10 dark:bg-white/10 dark:text-slate-100'
          : 'text-muted-foreground hover:text-orange-600 dark:text-slate-300 dark:hover:text-orange-400'
      }`}
    >
      <List className="h-3.5 w-3.5" />
      Boards
    </button>
    {breadcrumbTrail.map((crumb, index) => (
      <div key={crumb.id} className="inline-flex items-center gap-2">
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        <button
          type="button"
          onClick={() => onJump(index)}
          className={`rounded-lg px-2 py-1 ${index === breadcrumbTrail.length - 1
            ? 'border border-border bg-muted font-medium text-foreground dark:border-white/10 dark:bg-white/10'
            : 'text-muted-foreground transition-colors hover:text-orange-600 dark:text-slate-300 dark:hover:text-orange-400'
            }`}
        >
          {crumb.board_name}
        </button>
      </div>
    ))}
  </div>
);

type DetailPanelProps = {
  selectedBoard: BoardTreeNode;
  detailTab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
};

export const BoardsDetailPanel = ({
  selectedBoard,
  detailTab,
  onTabChange,
}: DetailPanelProps) => (
  <div className="rounded-[20px] border border-border bg-card p-6 dark:border-white/10 dark:bg-black/70">
    <p className="text-base text-muted-foreground">
      {selectedBoard.description || 'No description'}
    </p>
    <div className="mt-6 flex flex-wrap gap-6 border-b border-border pb-3 dark:border-white/10">
      <button
        type="button"
        onClick={() => onTabChange('overview')}
        className={`pb-2 text-base font-medium ${detailTab === 'overview'
          ? 'border-b-2 border-orange-500 text-orange-500'
          : 'text-muted-foreground hover:text-foreground'
          }`}
      >
        Overview & Sub-Boards
      </button>
      <button
        type="button"
        onClick={() => onTabChange('courses')}
        className={`pb-2 text-base font-medium ${detailTab === 'courses'
          ? 'border-b-2 border-orange-500 text-orange-500'
          : 'text-muted-foreground hover:text-foreground'
          }`}
      >
        Courses
      </button>
      <button
        type="button"
        onClick={() => onTabChange('projects')}
        className={`pb-2 text-base font-medium ${detailTab === 'projects'
          ? 'border-b-2 border-orange-500 text-orange-500'
          : 'text-muted-foreground hover:text-foreground'
          }`}
      >
        Projects
      </button>
      <button
        type="button"
        onClick={() => onTabChange('people-access')}
        className={`pb-2 text-base font-medium ${detailTab === 'people-access'
          ? 'border-b-2 border-orange-500 text-orange-500'
          : 'text-muted-foreground hover:text-foreground'
          }`}
      >
        People & Access
      </button>
    </div>
  </div>
);

type EmptyStateProps = {
  title: string;
  heading: string;
  description: string;
  sectionIcon?: ReactNode;
  showCreateAction?: boolean;
  createActionLabel?: string;
  icon?: ReactNode;
  onOpenCreate: () => void;
};

export const BoardsEmptyState = ({
  title,
  heading,
  description,
  sectionIcon,
  showCreateAction = false,
  createActionLabel = 'Create new Sub-board',
  icon,
  onOpenCreate,
}: EmptyStateProps) => (
  <div className="space-y-4">
    <h3 className="flex items-center gap-2 text-2xl font-semibold">
      {sectionIcon ?? <Layers className="h-6 w-6 text-muted-foreground dark:text-slate-300" />}
      {title}
    </h3>
    <div className="rounded-[30px] border border-border bg-card px-6 py-20 text-center dark:border-white/10 dark:bg-black/70">
      <div className="mx-auto flex max-w-md flex-col items-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-border bg-muted dark:border-white/10 dark:bg-white/5">
          {icon ?? <Layers className="h-7 w-7 text-muted-foreground dark:text-slate-400" />}
        </div>
        <p className="text-3xl leading-tight font-semibold text-foreground sm:text-4xl dark:text-slate-100">
          {heading}
        </p>
        <p className="mt-2 text-base text-muted-foreground dark:text-slate-400">{description}</p>
        {showCreateAction && (
          <button
            type="button"
            className="group mt-7 inline-flex items-center gap-2 text-xl font-semibold text-orange-600 transition-colors hover:text-orange-500 dark:text-orange-500 dark:hover:text-orange-400"
            onClick={onOpenCreate}
          >
            <Plus className="h-5 w-5" />
            <span className="group-hover:underline">{createActionLabel}</span>
          </button>
        )}
      </div>
    </div>
  </div>
);

type CardsSectionProps = {
  currentBoardId?: number;
  visibleNodes: Board[];
  rootBoardsTree: BoardTreeNode[];
  canManage: boolean;
  isDeleting: boolean;
  onOpenBoard: (node: BoardTreeNode) => void;
  onOpenEdit: (board: BoardEditorItem) => void;
  onDelete: (board: Pick<Board, 'id' | 'board_name'>) => void;
};

export const BoardsCardsSection = ({
  currentBoardId,
  visibleNodes,
  rootBoardsTree,
  canManage,
  isDeleting,
  onOpenBoard,
  onOpenEdit,
  onDelete,
}: CardsSectionProps) => {
  const findNodeById = (nodes: BoardTreeNode[], nodeId: number): BoardTreeNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      const childFound = findNodeById(node.children ?? [], nodeId);
      if (childFound) return childFound;
    }
    return null;
  };

  return (
    <>
      <h3 className="flex items-center gap-2 text-3xl font-semibold">
        <Layers className="h-6 w-6 text-muted-foreground dark:text-slate-300" />
        {currentBoardId ? 'Sub-Boards' : 'Faculties & Departments'}
      </h3>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {visibleNodes.map((board) => (
          <Card
            key={board.id}
            className="group h-full min-h-[180px] rounded-3xl border border-border bg-card shadow-sm transition-colors dark:border-white/10 dark:bg-gradient-to-b dark:from-neutral-900 dark:to-black dark:text-white dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
          >
            <CardContent
              className="relative flex h-full cursor-pointer flex-col justify-between p-7"
              onClick={() => {
                const fromTree = findNodeById(rootBoardsTree, board.id);
                if (fromTree) {
                  onOpenBoard(fromTree);
                  return;
                }
                onOpenBoard({
                  id: board.id,
                  children: [],
                  board_head: {
                    head_of_board: board.head_of_board,
                    first_name: '',
                    last_name: '',
                    photo_url: null,
                    email: '',
                  },
                  is_member: false,
                  board_name: board.board_name,
                  description: board.description,
                  start_date: board.start_date,
                  end_date: board.end_date,
                  is_active: board.is_active,
                  academic_year: board.academic_year,
                  level: board.level,
                  head_of_board: board.head_of_board,
                  parent: board.parent,
                  number_of_users: board.overall_students ?? 0,
                  number_of_courses: board.courses ?? 0,
                });
              }}
            >
              {canManage && (
                <div className="absolute top-4 right-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="rounded-full border border-border bg-muted p-2 hover:bg-muted/80 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
                        aria-label={`Actions for ${board.board_name}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Ellipsis className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          onOpenEdit({
                            id: board.id,
                            board_name: board.board_name,
                            description: board.description,
                            head_of_board: board.head_of_board,
                            parent: board.parent ?? null,
                          })
                        }
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() =>
                          onDelete({
                            id: board.id,
                            board_name: board.board_name,
                          })
                        }
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-muted dark:border-white/10 dark:bg-white/5">
                  <Building2 className="h-6 w-6 text-foreground dark:text-white/90" />
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="line-clamp-2 text-xl font-semibold leading-snug transition-colors group-hover:text-orange-600 dark:group-hover:text-orange-400">
                    {board.board_name}
                  </CardTitle>
                  <p className="mt-2 text-sm text-muted-foreground transition-colors group-hover:text-foreground dark:text-slate-300 dark:group-hover:text-orange-300">
                    {board.childrens ?? 0} sub-boards
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage
                        src={board.board_head?.photo_url || undefined}
                        alt={`${board.board_head?.first_name ?? ''} ${board.board_head?.last_name ?? ''}`.trim() || 'Head of board'}
                      />
                      <AvatarFallback>
                        {`${board.board_head?.first_name?.[0] ?? ''}${board.board_head?.last_name?.[0] ?? ''}`.toUpperCase() || 'HB'}
                      </AvatarFallback>
                    </Avatar>
                    <p className="truncate text-sm text-muted-foreground transition-colors group-hover:text-foreground dark:text-slate-300 dark:group-hover:text-orange-300">
                      {`${board.board_head?.first_name ?? ''} ${board.board_head?.last_name ?? ''}`.trim() || 'Head of board'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

type HierarchySectionProps = {
  isTreeLoading: boolean;
  treeError: unknown;
  treeNodesForHierarchy: BoardTreeNode[];
  renderTreeNodes: (nodes: BoardTreeNode[]) => ReactNode;
};

export const BoardsHierarchySection = ({
  isTreeLoading,
  treeError,
  treeNodesForHierarchy,
  renderTreeNodes,
}: HierarchySectionProps) => (
  <Card className="overflow-hidden rounded-3xl border border-border bg-card dark:border-white/10 dark:bg-black/80">
    <div className="border-b border-border bg-muted/40 px-4 py-3 dark:border-white/10 dark:bg-white/5">
      <h3 className="flex items-center gap-2 text-base font-semibold text-foreground dark:text-slate-300">
        <PanelLeft className="h-4 w-4" />
        Hierarchical Overview
      </h3>
    </div>
    <CardContent className="p-2">
      {isTreeLoading ? (
        <div className="p-6 text-sm text-muted-foreground dark:text-slate-400">Loading hierarchy...</div>
      ) : treeError ? (
        <div className="p-6 text-sm text-red-500">
          Unable to load board hierarchy.
        </div>
      ) : treeNodesForHierarchy.length ? (
        <div className="py-2">{renderTreeNodes(treeNodesForHierarchy)}</div>
      ) : (
        <div className="p-6 text-sm text-muted-foreground dark:text-slate-400">
          No hierarchy data available.
        </div>
      )}
    </CardContent>
  </Card>
);

type CoursesSectionProps = {
  courses: Course[];
  onOpenCourse: (courseId: number) => void;
  onOpenCreate: () => void;
  canManage: boolean;
};

export const BoardsCoursesSection = ({
  courses,
  onOpenCourse,
  onOpenCreate,
  canManage,
}: CoursesSectionProps) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 text-2xl font-semibold">
        <BookOpen className="h-6 w-6 text-muted-foreground dark:text-slate-300" />
        Courses
      </h3>
      {canManage && <Button onClick={onOpenCreate}><Plus className="h-4 w-4" /> Add course</Button>}
    </div>
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {courses.map((course) => (
        <Card
          key={course.id}
          className="group h-full min-h-[168px] rounded-3xl border border-border bg-card shadow-sm transition-colors dark:border-white/10 dark:bg-black/70"
        >
          <CardContent
            className="flex h-full cursor-pointer flex-col justify-between p-7"
            onClick={() => onOpenCourse(course.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenCourse(course.id);
              }
            }}
          >
            <div className="flex items-start gap-3">
              <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-muted dark:border-white/10 dark:bg-white/5">
                <BookOpen className="h-5 w-5 text-foreground dark:text-slate-200" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="line-clamp-2 text-lg font-semibold leading-snug transition-colors group-hover:text-orange-600 dark:group-hover:text-orange-400">
                  {course.course_name}
                </CardTitle>
                <p className="mt-1 truncate text-sm text-muted-foreground">
                  {course.course_code || 'No course code'}
                </p>
                <p className="mt-3 text-sm text-muted-foreground">
                  {course.students_enrolled} learners
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

type ProjectsSectionProps = {
  projects: ProjectResponse[];
  onOpenProject: (projectId: number) => void;
  onOpenCreate: () => void;
  canManage: boolean;
};

export const BoardsProjectsSection = ({ projects, onOpenProject, onOpenCreate, canManage }: ProjectsSectionProps) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 text-2xl font-semibold">
        <Layers className="h-6 w-6 text-muted-foreground dark:text-slate-300" />
        Projects
      </h3>
      {canManage && <Button onClick={onOpenCreate}><Plus className="h-4 w-4" /> Add project</Button>}
    </div>
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {projects.map((project) => (
        <Card key={project.id} className="cursor-pointer rounded-3xl border border-border bg-card dark:border-white/10 dark:bg-black/70" onClick={() => onOpenProject(project.id)}>
          <CardContent className="p-7">
            <CardTitle className="line-clamp-2 text-lg">{project.name}</CardTitle>
            <p className="mt-3 text-sm text-muted-foreground">{project.project_duration} days</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

export type BoardPersonRole = 'Head of board' | 'Instructor' | 'Learner';
export type BoardPersonType = 'head' | 'teacher' | 'student';

export type BoardPersonItem = {
  key: string;
  personId: string;
  firstName: string;
  lastName: string;
  email: string;
  photoUrl?: string | null;
  role: BoardPersonRole;
  type: BoardPersonType;
  active: boolean;
  mappingId?: number;
};

type PeopleAccessSectionProps = {
  people: BoardPersonItem[];
  canManage: boolean;
  onOpenAddUsers: () => void;
  onMarkInactive: (person: BoardPersonItem) => void;
  onRemove: (person: BoardPersonItem) => void;
  isMarkingInactive: boolean;
  isRemoving: boolean;
};

export const BoardPeopleAccessSection = ({
  people,
  canManage,
  onOpenAddUsers,
  onMarkInactive,
  onRemove,
  isMarkingInactive,
  isRemoving,
}: PeopleAccessSectionProps) => {
  const roleOrder: Record<BoardPersonRole, number> = {
    'Head of board': 0,
    Instructor: 1,
    Learner: 2,
  };
  const sortedPeople = [...people].sort(
    (a, b) => roleOrder[a.role] - roleOrder[b.role]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-2xl font-semibold">
          <Users className="h-6 w-6 text-muted-foreground dark:text-slate-300" />
          People & Access
        </h3>
        {canManage ? (
          <Button onClick={onOpenAddUsers}>
            <Plus className="h-4 w-4" />
            Add users
          </Button>
        ) : null}
      </div>
      <div className="grid items-start grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {sortedPeople.map((person) => {
          const fullName = `${person.firstName} ${person.lastName}`.trim() || person.email;
          const isHead = person.type === 'head';
          return (
            <div
              key={person.key}
              className={`overflow-hidden rounded-xl border bg-background shadow-sm min-h-[220px] ${
                person.active ? '' : 'opacity-60 saturate-50'
              }`}
            >
              <div className="flex h-20 items-start justify-between bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3 text-sm text-white/90">
                <span>{person.role}</span>
                {canManage && !isHead && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/15"
                      >
                        <Ellipsis className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onMarkInactive(person)}
                        disabled={isMarkingInactive || !person.active}
                      >
                        Mark as Inactive
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => onRemove(person)}
                        disabled={isRemoving}
                      >
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className="-mt-10 flex flex-col items-center px-4 pb-5 text-center">
                <Avatar className="h-20 w-20 border-4 border-background">
                  <AvatarImage src={person.photoUrl || undefined} alt={fullName} />
                  <AvatarFallback>
                    {getInitials(person.firstName || '', person.lastName || '') || 'U'}
                  </AvatarFallback>
                </Avatar>
                <p className="mt-3 text-lg font-semibold">{fullName}</p>
                <p className="break-all text-sm text-muted-foreground">{person.email}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type AddUsersDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: User[];
  selectedIds: string[];
  onToggleUser: (userId: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onSave: () => void;
  isSaving: boolean;
};

export const AddBoardUsersDrawer = ({
  open,
  onOpenChange,
  users,
  selectedIds,
  onToggleUser,
  search,
  onSearchChange,
  onSave,
  isSaving,
}: AddUsersDrawerProps) => {
  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = users.filter((user) => {
    const fullName = `${user.profile?.first_name ?? ''} ${user.profile?.last_name ?? ''}`
      .trim()
      .toLowerCase();
    return (
      fullName.includes(normalizedSearch) ||
      user.email.toLowerCase().includes(normalizedSearch)
    );
  });

  const resolveRole = (user: User) => {
    const normalizedRoles = (user.user_in_client_roles ?? []).map((role) =>
      role.trim().toUpperCase().replace(/\s+/g, '_')
    );
    if (normalizedRoles.includes('STUDENT')) return 'Learner';
    return 'Instructor';
  };

  const allVisibleSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedIds.includes(user.id));

  const handleSelectAllChange = (checked: boolean | 'indeterminate') => {
    const shouldSelectAll = checked === true;

    if (shouldSelectAll) {
      filteredUsers.forEach((user) => {
        if (!selectedIds.includes(user.id)) {
          onToggleUser(user.id);
        }
      });
      return;
    }

    filteredUsers.forEach((user) => {
      if (selectedIds.includes(user.id)) {
        onToggleUser(user.id);
      }
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0">
        <SheetHeader className="border-b">
          <SheetTitle>Add users to board</SheetTitle>
          <SheetDescription>
            Select instructors and learners to add on this board.
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 border-b">
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by name or email"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={handleSelectAllChange}
              />
              <span className="text-sm font-medium">Select all</span>
            </label>
            <span className="text-xs text-muted-foreground">
              {filteredUsers.length} shown
            </span>
          </div>

          {filteredUsers.map((user) => {
            const fullName = `${user.profile?.first_name ?? ''} ${user.profile?.last_name ?? ''}`.trim();
            const roleLabel = resolveRole(user);
            const checked = selectedIds.includes(user.id);
            return (
              <label
                key={user.id}
                className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/40"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggleUser(user.id)}
                />
                <Avatar className="h-10 w-10">
                  <AvatarImage
                    src={user.profile?.photo_url || user.profile?.thumbnail || undefined}
                    alt={fullName || user.email}
                  />
                  <AvatarFallback>
                    {getInitials(user.profile?.first_name || '', user.profile?.last_name || '') || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{fullName || user.email}</p>
                  <p className="truncate text-sm text-muted-foreground">{user.email}</p>
                </div>
                <span className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                  {roleLabel}
                </span>
              </label>
            );
          })}
          {!filteredUsers.length && (
            <p className="text-sm text-muted-foreground">No users found.</p>
          )}
        </div>

        <SheetFooter className="border-t">
          <Button onClick={onSave} disabled={!selectedIds.length || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

type FormDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  editingBoard: BoardEditorItem | null;
  form: BoardFormState;
  setForm: React.Dispatch<React.SetStateAction<BoardFormState>>;
  users: User[];
  parentOptions: Board[];
  onCancel: () => void;
  onSave: () => void;
  isSubmitting: boolean;
};

export const BoardFormDialog = ({
  open,
  setOpen,
  editingBoard,
  form,
  setForm,
  users,
  parentOptions,
  onCancel,
  onSave,
  isSubmitting,
}: FormDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      if (!next) onCancel();
      setOpen(next);
    }}
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {editingBoard ? 'Edit board' : 'Create board'}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="board_name">Board name</Label>
          <Input
            id="board_name"
            value={form.board_name}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, board_name: event.target.value }))
            }
            placeholder="e.g. Academic Board"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="head_of_board">Head of board</Label>
          <Select
            value={form.head_of_board}
            onValueChange={(value) =>
              setForm((prev) => ({ ...prev, head_of_board: value }))
            }
          >
            <SelectTrigger id="head_of_board" className="w-full">
              <SelectValue placeholder="Select user" />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {`${user.profile?.first_name ?? ''} ${user.profile?.last_name ?? ''}`.trim() || user.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="parent_board">Parent board</Label>
          <Select
            value={form.parent}
            onValueChange={(value) =>
              setForm((prev) => ({ ...prev, parent: value }))
            }
          >
            <SelectTrigger id="parent_board" className="w-full">
              <SelectValue placeholder="Select parent board" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Top level</SelectItem>
              {parentOptions.map((board) => (
                <SelectItem key={board.id} value={String(board.id)}>
                  {board.board_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={form.description}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, description: event.target.value }))
            }
            placeholder="Enter board description"
            rows={4}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : editingBoard ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
